import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { catchError, map, tap, mergeMap } from "rxjs/operators";

// Env
import { environment } from "src/environments/environment";

// Utils
import { api } from "../utils/api";
import { constants } from "../utils/constants";
import helpers from "../utils/helpers";

// Model
import { RequestModel } from "../models/request.model";
import { Receipt } from "../models/receipt.model";
import { UserLogged } from "../models/user-logged.model";
import {
  RequestState,
  Created,
  Accepted,
  Approved,
} from "src/app/models/request-state.model";
import { moment } from "ngx-bootstrap/chronos/test/chain";

@Injectable({
  providedIn: "any",
})
export class RequestService {
  //API
  private HISTORY_URL = environment.API_URL + api.requests.getRequestHistory;
  private DETAIL_URL = environment.API_URL + api.requests.getRequestByNumber;
  private DOWNLOAD_URL = environment.API_URL + api.requests.download;
  private GET_COMPANY = environment.API_URL + api.account.create.getCompanyData;
  private CREATE_URL = environment.API_URL + api.requests.createRequest;
  private UPDATE_URL = environment.API_URL + api.requests.updateRequestState;
  private EMAIL_URL = environment.API_URL + api.users.email;
  private GET_EMAILS_URL = environment.API_URL + api.users.getEmails;
  private CREATE_RECEIPT_URL =
    environment.API_URL + api.requests.receipts.create;
  private DELETE_RECEIPT_URL =
    environment.API_URL + api.requests.receipts.delete;
  private UPDATE_RECEIPT_URL =
    environment.API_URL + api.requests.receipts.update;
  private RECEIPT_PDF_URL = environment.API_URL + api.requests.receipts.pdf;
  private BANK_LIST_URL = environment.API_URL + api.requests.receipts.bankList;
  private PAYMENT_URL = environment.API_URL + api.requests.payment;

  requestSearch: RequestModel[] = [];
  requestHistory: RequestModel[] = [];
  dirty: boolean = true;
  bankList: string[] = [];

  //File processing
  file: File = null;
  type: number;
  shouldResetFile = true;
  request: RequestModel = null;
  userLogged: UserLogged = null;
  digitsRegex = /^\d+$/;

  constructor(private httpClient: HttpClient) {}

  reset() {
    this.file = null;
    this.type = null;
  }

  refresh() {
    this.dirty = true;
  }

  resetHistory() {
    this.requestHistory = [];
  }

  resetSearch() {
    this.requestSearch = [];
  }

  setCurrentRequest(request: RequestModel) {
    this.request = request;
  }

  // =================================================================
  //                            API
  // =================================================================
  public getRequestHistory(ruc: number): Observable<RequestModel[]> {
    if (!this.dirty && this.requestHistory.length > 0) {
      return of(this.requestHistory);
    }
    this.dirty = false;

    const body = {
      numeroIdentificacion: ruc,
      tipoIdentificacion: 6,
      filters: {
        limit: 3,
      },
    };

    return this.httpClient.post(this.HISTORY_URL, body).pipe(
      map((resp: any) => {
        if (!resp) throw constants.alerts.noResults.emptySearch;
        this.requestHistory = resp.map((req) => this.mapRequest(req));
              
        return this.requestHistory;
      }),
      catchError((err) => {
        if (err.type) {
          throw err;
        }
        throw this.getError(err.error?.code);
      })
    );
  }

  searchRequests(data: {
    ruc: number;
    limit: number;
    number?: number;
    typeCode?: number;
    stateCode?: number;
    date?: string;
  }): Observable<RequestModel[]> {
    this.requestSearch = [];

    const body = {
      numeroIdentificacion: data.ruc,
      tipoIdentificacion: 6,
      filters: {
        limit: data.limit,
        numeroSolicitud: data.number || null,
        codeTipoSolicitud: data.typeCode || null,
        codeEstado: data.stateCode || null,
        fechaCarga: data.date || null,
      },
    };

    return this.httpClient.post(this.HISTORY_URL, body).pipe(
      map((resp: any) => {

        if (!resp) throw constants.alerts.noResults.emptySearch;
           this.requestSearch = resp.map((req) => this.mapRequest(req));
         
           return this.requestSearch;
      }),
      catchError((err) => {
       
        if (err.type) {
          throw err;
        }
        throw this.getError(err.error?.code);
      })
    );
  }

  getRequestByNumber(number: number) {
    const params = new HttpParams().append(
      "solicitudNumber",
      number.toString()
    );

    return this.httpClient.get(this.DETAIL_URL, { params }).pipe(
      map((resp: any) => {
        if (resp.code === "002") {
          return resp.data;
        }
      }),
      catchError((err) => {
        throw new Error(err);
      })
    );
  }

  createRequest(user: UserLogged): Observable<any> {
    const formData: FormData = this.getCreateRequestBody(user);
    return this.httpClient.post(this.CREATE_URL, formData).pipe(
      tap((resp: any) => {
        this.refresh();
        this.request.number = resp.numero;
        this.notificateByEmail(new Created(), user, this.request);
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  updateState(
    request: RequestModel,
    newState: RequestState,
    userLogged: UserLogged
  ): Observable<any> {
    const url = this.UPDATE_URL + request.id;
    const body = {
      estado: newState.getCode(),
    };

    return this.httpClient.patch(url, body).pipe(
      tap(() => {
        this.notificateByEmail(newState, userLogged, request);
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  /**
   * Crear el desembolso y luego actualizar el estado a "Confirmada" con el medio de pago seleccionado
   */
  confirmRequest(
    request: RequestModel,
    paymentMethod: number,
    userLogged: UserLogged
  ) {
    const body = {
      solicitud_id: request.id,
      fecha: new Date().toISOString().slice(0, 10),
      tipo_medio_de_pago: paymentMethod,
      monto: request.amount,
      usuario: userLogged.getUserData().email,
    };
    return this.httpClient.post(this.PAYMENT_URL, body).pipe(
      mergeMap(() => this.updateState(request, new Accepted(), userLogged)),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  // =================================================================
  //                          Receipts
  // =================================================================
  createReceipt(request: RequestModel, receipt: Receipt, user: UserLogged) {
    const formData: FormData = this.getCreateReceiptBody(
      request,
      receipt,
      user
    );
    return this.httpClient.post(this.CREATE_RECEIPT_URL, formData).pipe(
      map((resp: any) => {
        return {
          id: resp.comprobante_id,
          url: resp.referencia_url,
          number: resp.num_comprobante,
        };
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  updateReceipt(request: RequestModel, receipt: Receipt) {
    const formData: FormData = this.getUpdateReceiptBody(request, receipt);
    return this.httpClient.patch(this.UPDATE_RECEIPT_URL, formData).pipe(
      catchError((err) => {
      
        throw constants.alerts.default;
      })
    );
  }

  deleteReceipt(receipt: Receipt) {
    const url = this.DELETE_RECEIPT_URL + receipt.id;
    return this.httpClient.delete(url).pipe(
      catchError((err) => {
       
        throw constants.alerts.default;
      })
    );
  }

  getBankList(userLogged: UserLogged) {
    if (this.bankList.length > 0) {
      return of(this.bankList);
    }

    const body = {
      identificationNumber: userLogged.getUserData().idNumber,
      identificationType: "RUC",
    };
    return this.httpClient.post(this.BANK_LIST_URL, body).pipe(
      map((resp: any) => {
        if (resp.Detail) {
          throw new Error();
        }
        this.bankList = resp.collection.institution.map(
          (bank: any) => bank.shortDesc
        );
        return this.bankList;
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  async getCompanyNameByRuc(ruc) {
    const url = this.GET_COMPANY + ruc;
    return this.httpClient
      .get(url)
      .pipe(
        map((resp: any) => {
          if (!resp?.enterpriseCustomer) {
            throw new Error();
          }
          return resp.enterpriseCustomer.businessName;
        }),
        catchError((err) => {
          throw constants.alerts.default;
        })
      )
      .toPromise();
  }

  // =================================================================
  //                      Email notifications
  // =================================================================
  private notificateByEmail(
    state: RequestState,
    userLogged: UserLogged,
    request: RequestModel
  ) {
    let role = "";
    switch (+state.getCode()) {
      case constants.rules.codes.requestStates.created.value: //Created = SUPERVISOR
        role = constants.rules.codes.roles.supervisor.name;
        break;

      case constants.rules.codes.requestStates.pending.value: //Pending = Tesorero
        role = constants.rules.codes.roles.treasurer.name;
        break;

      case constants.rules.codes.requestStates.approved.value: //Approved = OPERADOR_BANCO
        role = constants.rules.codes.roles.admin.name; //en la db no estan diferenciados
        break;

      case constants.rules.codes.requestStates.rejected.value: //Rejected = PLANILLERO
        role = constants.rules.codes.roles.responsibleForRequests.name;
        break;
    }
    const body = {
      roleType: role,
      institutionId: userLogged.getUserData().companyId,
    };

    this.httpClient
      .post(this.GET_EMAILS_URL, body)
      .pipe(
        map((resp: any) => {
          try {
            return resp.collection?.systemUser?.map((user) => {
              return this.mapSystemUser(user, role);
            });
          } catch (error) {
            const listOfUsers = [];
            listOfUsers.push(
              this.mapSystemUser(resp.collection?.systemUser, role)
            );
            return listOfUsers;
          }
        })
      )
      .subscribe((listOfUsers: any[]) =>
        listOfUsers
          .filter(
            (user) =>
              user.role !== constants.rules.codes.roles.admin.name ||
              (user.role === constants.rules.codes.roles.admin.name &&
                user.administrator)
          )
          .forEach((user) => this.sendEmail(user, userLogged, request, state))
      );

      if(state.getName() === "APROBADA"){
           this.sendEmailOpBanco( userLogged, request,  state as Approved);
      }
  }

  private mapSystemUser(systemUser: any, role: string) {
    return {
      administrator: systemUser?.administrator,
      firstName: systemUser?.customer?.firstName1,
      lastName: systemUser?.customer?.lastName1,
      docNumber: systemUser?.customer?.identificationNumber,
      docType: systemUser?.customer?.shortDesc,
      email: systemUser?.electronicsContact?.emailAddressComplete,
      charge: systemUser?.userPosition?.description,
      phone: systemUser?.contactPhone?.phoneNumber,
      role,
    };
  }

  private sendEmail(
    userData: any,
    userLogged: UserLogged,
    request: RequestModel,
    newState: RequestState
  ) {
    const body = {
      firstNameSource: helpers.toTitleCase(userData.firstName)+" "+helpers.toTitleCase(userData.lastName),
      lastNameSource: helpers.toTitleCase(userData.lastName),
      identificationNumberSource: userData.docNumber,
      identificationTypeSource: userData.docType,
      emailAddressComplete: userData.email,
      firstNameTarget: userLogged.getUserData().companyName,
      identificationNumberTarget: userLogged.getUserData().idNumber,
      identificationTypeTarget: userLogged.getUserData().idType,
      executionDate: helpers.internalFormatFromDate(new Date()),
      templateId: newState.getTemplateId(),
      roleTypeOperator:
        userData.role == constants.rules.codes.roles.responsibleForRequests.name
          ? "Responsable de solicitudes"
          : helpers.toTitleCase(userData.role),
      idRequest: request.number,
      totalAmount: request.getAmountInEmailFormat(),
      numberVouchers: request.receipts?.length,
      statusRequest: helpers.toTitleCase(newState.getName()),
    };

   
    this.httpClient.post(this.EMAIL_URL, body).subscribe();

  }

  private sendEmailOpBanco(
    userLogged: UserLogged,
    request: RequestModel,
    newState: Approved
  ) {

    const body = {
      firstNameSource: userLogged.getUserData().companyName,
      lastNameSource: "",
      identificationNumberSource: "",
      identificationTypeSource: "",
      emailAddressComplete: "operadorextranet1@bancofalabella.com.pe",
      firstNameTarget: userLogged.getUserData().companyName,
      identificationNumberTarget: userLogged.getUserData().idNumber,
      identificationTypeTarget: userLogged.getUserData().idType,
      executionDate: helpers.internalFormatFromDate(new Date()),
      templateId: "BFPE_EXTRANET_SOLICITUD_APROBADA",
      roleTypeOperator:"",
      idRequest: request.number,
      totalAmount: request.getAmountInEmailFormat(),
      numberVouchers: request.receipts?.length,
      statusRequest: helpers.toTitleCase(newState.getName()),
    };
    
    this.httpClient.post(this.EMAIL_URL, body).subscribe();
  }

  // =================================================================
  //                          Errors
  // =================================================================
  /**
   * Obtiene el error correspondiente desde response
   */
  private getError(errorCode: string) {
    switch (errorCode) {
      case "0007":
        return constants.alerts.noResults.emptySearch;

      default:
        return constants.alerts.noResults.unexpected;
    }
  }

  // =================================================================
  //                      File Downloads
  // =================================================================
  /**
   * Descarga de la solicitud en TXT
   */
  downloadRequestFile(request: RequestModel) {
    const data: FormData = new FormData();
    data.append("path", request.url);
    data.append("fileName", request.name);

    return this.httpClient.post(this.DOWNLOAD_URL, data).pipe(
      map((resp: any) => {
        const base64 = resp.file;
        const fileBlob = this.dataURItoBlob(base64, "text/plain");
        const file = new File([fileBlob], resp.fileName, {
          type: "text/plain",
        });
        return window.URL.createObjectURL(file);
      }),
      catchError((err) => {
        throw constants.alerts.default;
      })
    );
  }

  /**
   * Descarga del comprobante en PDF
   */
  downloadReceiptPdf(number: number) {
    let url = this.RECEIPT_PDF_URL + number;
    return this.httpClient.get(url).pipe(
      map((resp: any) => {
        const base64 = resp.document.fileBase64;
        const fileBlob = this.dataURItoBlob(base64, "application/pdf");
        const file = new File([fileBlob], resp.fileName, {
          type: "application/pdf",
        });
        return window.URL.createObjectURL(file);
      }),
      catchError((err) => {
        throw constants.alerts.noResults.unexpected;
      })
    );
  }

  // =================================================================
  //                      File Processing
  // =================================================================
  /**
   * Entrypoint del procesamiento del .txt, invoca al método correspondiente dependiendo el Type recibido como parámetro
   */
  async processFile(
    file: File,
    type: number,
    user: UserLogged
  ): Promise<RequestModel> {
    this.file = file;
    this.type = type;
    this.userLogged = user;
    //Validar nombre
    if (!this.validName() ) throw new Error("Invalid file name");
    switch (+type) {
      case constants.rules.codes.requestTypes.rrbb.value:
        return this.processRRBB(await this.getFileContent(file));
      case constants.rules.codes.requestTypes.cts.value:
        return this.processCTS(await this.getFileContent(file));
      case constants.rules.codes.requestTypes.paymentOfSalaries.value:
        return this.processPaymentOfSalaries(await this.getFileContent(file));
    }
  }

  /**
   * Procesa el archivo .txt para Pago de haberes
   */
  async processPaymentOfSalaries(lines: string[]): Promise<RequestModel> {
    const header = lines.shift();
    // Validar cabecera
    if (header.length !== 107 || !header.startsWith("#")) {
      throw new Error("Invalid Header");
    }
    let currency = header.slice(24, 26);
    let amount = header.slice(26, 41).replace(/^0+/, "");
    let floatAmount = parseFloat(
      amount.slice(0, amount.length - 2) + "." + amount.slice(amount.length - 2)
    );
    let totalRecords = parseInt(header.slice(84, 90).replace(/^0+/, ""));

    /*
     * Validacion de columnas del header
     * 1- Campos numéricos, 2- Campos no numéricos, 3- Sólo dígitos
     */
    if (
      isNaN(parseInt(amount) as any) ||
      isNaN(totalRecords) ||
      isNaN(header.slice(69, 84) as any) ||
      !isNaN(header.slice(2, 3) as any) ||
      !isNaN(header.slice(3, 4) as any) ||
      !isNaN(currency as any) ||
      !header.slice(1, 2).match(this.digitsRegex) ||
      !header.slice(4, 24).match(this.digitsRegex) ||
      !header.slice(41, 49).match(this.digitsRegex)
    ) {
      
      throw new Error("Invalid Header");
    }

    // Obtener Nombre de empresa y validarlo
    const companyName = await this.getCompanyNameByRuc(
      this.getFileNameParts()[1]
    );

    //Validacion de nombre de Empresa en profiling contra BD local de Perú
    //if (
    //  companyName.replace(" ", "").toUpperCase().trim() !==
    //  this.userLogged
    //   .getUserData()
    //    .companyName.replace(" ", "")
    //    .toUpperCase()
    //    .trim()
    //)
    //  throw new Error("Invalid company name");

    let totalAmount = 0;

    // Validar registros
    lines.forEach((line) => {
      /*
       * Validacion de columnas de registros
       * 1- Campos numéricos, 2- Campos no numéricos, 3- Sólo dígitos
       */
      if (
        line.length !== 137 ||
        !line.startsWith(" ") ||
        isNaN(line.slice(65, 80) as any) ||
        !isNaN(line.slice(2, 3) as any) ||
        !isNaN(line.slice(23, 63) as any) ||
        !isNaN(line.slice(63, 65) as any) ||
        !isNaN(line.slice(80, 120) as any) ||
        !isNaN(line.slice(121, 124) as any) ||
        !line.slice(1, 2).match(this.digitsRegex) ||
        !line.slice(3, 23).trim().match(this.digitsRegex) ||
        !line.slice(120, 121).match(this.digitsRegex) ||
        !line.slice(136, 137).match(this.digitsRegex)
      ) {
        throw new Error(
          `Invalid record -> Doc. Number: ${line.slice(124, 136)}`
        );
      }
      amount = line.slice(65, 80).replace(/^0+/, "");
      totalAmount += parseFloat(
        amount.slice(0, amount.length - 2) +
          "." +
          amount.slice(amount.length - 2)
      );
    });

    if (floatAmount != totalAmount) {
      totalAmount = parseFloat(totalAmount.toFixed(2));

      if (floatAmount != totalAmount) {
          throw new Error("Invalid amount");
      }
    }

    this.request = new RequestModel({
      type: this.type,
      currency: currency === "US" ? "Dólares" : "Soles",
      totalRecords,
      amount: floatAmount,
      name: this.file.name,
      ruc: this.getFileNameParts()[1],
      companyName: this.userLogged.getUserData().companyName,
    });

    return this.request;
  }

  /**
   * Procesa el archivo .txt para CTS
   */
  private processCTS(lines: string[]): RequestModel {
    console.log('----processCTS----');
    const header = lines.shift();
    // Validar cabecera
    if (header.length !== 83) {
      
      throw new Error("Invalid Header");
    }
    console.log('Validar cabecera: '+!(header.length !== 83));

    const ruc = header.slice(0, 11);
    const companyName = header.slice(11, 51);
    const bankCheck = header.slice(51, 54);
    const checkNumber = header.slice(54, 62);
    const currency = header.slice(62, 63);
    const amount = header.slice(63, 78).replace(/^0+/, "").replace(",", ".");
    const totalRecords = header.slice(78, 83).replace(/^0+/, "");

    const nameCompany = this.userLogged
    .getUserData()
    .companyName.replace(" ", "")
    .toUpperCase()
    .trim();

    const nameCompanydb = companyName.replace(" ", "").toUpperCase().trim();

    console.log('Validar Ruc: '+this.validRuc(ruc));
    console.log('Validar totalRecords: '+!!totalRecords.match(this.digitsRegex));
    console.log('Validar checkNumber: '+!!checkNumber.match(this.digitsRegex));
    console.log('Validar bankCheck: '+!!bankCheck.match(this.digitsRegex));
    console.log('Validar currency: '+!!currency.match(this.digitsRegex));


    if (
      !this.validRuc(ruc) ||
      !totalRecords.match(this.digitsRegex) ||
      !checkNumber.match(this.digitsRegex) ||
      !bankCheck.match(this.digitsRegex) ||
      !currency.match(this.digitsRegex) ||
      isNaN(amount as any)
    ) {
      throw new Error("Invalid Header");
    }
    

    let totalAmount = 0;
    // Validar registros
    lines.forEach((line) => {
      if ( //cuentas 11 digitos
        !line.slice(0, 2).match(this.digitsRegex) ||
        !line.slice(67, 80).match(this.digitsRegex) ||
        !line.slice(80, 81).match(this.digitsRegex) ||
        isNaN(line.slice(81, 96).replace(/^0+/, "").replace(",", ".") as any) ||
        isNaN(
          line.slice(96, 111).replace(/^0+/, "").replace(",", ".") as any
        ) ||
        !isNaN(line.slice(17, 67) as any)
      ) {

        if( //cuentas 12 digitos

        !line.slice(0, 2).match(this.digitsRegex) ||
        !line.slice(67, 79).match(this.digitsRegex) ||
        !line.slice(82, 83).match(this.digitsRegex) ||
        isNaN(line.slice(83, 98).replace(/^0+/, "").replace(",", ".") as any) ||
        isNaN(
          line.slice(97, 112).replace(/^0+/, "").replace(",", ".") as any
        ) ||
        !isNaN(line.slice(17, 67) as any)


        ){

          throw new Error(`Invalid record -> Doc. Number: ${line.slice(2, 17)}`);
        }else{

          totalAmount += parseFloat(
            line.slice(82, 97).replace(/^0+/, "").replace(",", ".")
          );

        }

      }else{

        totalAmount += parseFloat(
          line.slice(81, 96).replace(/^0+/, "").replace(",", ".")
        );

      }
    });

    if (parseFloat(amount) != totalAmount) {
      totalAmount = parseFloat(totalAmount.toFixed(2));

      if (parseFloat(amount) != totalAmount) {
        throw new Error("Invalid amount");
      }
    }

    this.request = new RequestModel({
      type: this.type,
      companyName: this.userLogged.getUserData().companyName,
      ruc,
      totalRecords: parseInt(totalRecords),
      amount: parseFloat(amount),
      currency: parseInt(currency) === 2 ? "Dólares" : "Soles",
      name: this.file.name,
    });
    return this.request;
  }

  /**
   * Procesa el archivo .txt para Actualización de remuneraciones
   */
  private processRRBB(lines: string[]): RequestModel {
    // FALTAN DATOS
    console.log('processRRBB');
    const header = lines.shift();
    // Validar cabecera
    if (header.length !== 56) {
      throw new Error("Invalid Header");
    }
    const ruc = header.slice(0, 11);
    const companyName = header.slice(11, 51);
    const totalRecords = header.slice(51, 56);
    if (
      !this.validRuc(ruc) ||
      !totalRecords.match(this.digitsRegex) ||
      !isNaN(companyName as any)
    ) {
      throw new Error("Invalid Header");
    }

    // Validar registros
    let  totalAmount = 0;
    lines.forEach((line) => {
            
      if ( //cuentas 11 digitos
        !line.slice(0, 2).match(this.digitsRegex) ||
        !line.slice(67, 80).match(this.digitsRegex) ||
        isNaN(parseFloat(line.slice(80, 95))) ||
        !isNaN(line.slice(17, 67) as any)
      ) {
        if( //cuentas 12 digitos
        !line.slice(0, 2).match(this.digitsRegex) ||
        !line.slice(67, 79).match(this.digitsRegex) ||
        isNaN(parseFloat(line.slice(81, 96))) ||
        !isNaN(line.slice(17, 67) as any)  

        ){

          throw new Error(`Invalid record -> Doc. Number: ${line.slice(2, 17)}`);

        }else{
          totalAmount += parseFloat(line.slice(81, 96));
        }

      
      }else{
          totalAmount += parseFloat(line.slice(80, 95));
      }
      
    });

    this.request = new RequestModel({
      type: this.type,
      totalRecords: parseInt(totalRecords),
      companyName: this.userLogged.getUserData().companyName,
      amount: totalAmount,
      currency: this.getFileNameParts()[2] === "ME" ? "Dólares" : "Soles",
      ruc,
      name: this.file.name,
    });

    return this.request;
  }

  // =================================================================
  //                          Validations
  // =================================================================
  /**
   * Valida que el nombre del archivo sea el correcto teniendo en cuenta el tipo de solicitud
   */
  private validName(): boolean {
    const parts = this.getFileNameParts();

    let vRuc = this.validRuc(parts[1]);

   

    switch (+this.type) {
      case constants.rules.codes.requestTypes.rrbb.value:
        return (
          parts[0] === "CarRRBBCTS" &&
          vRuc &&
          this.validCurrency(parts[2]) &&
          this.validDate(parts[3])
        );

      case constants.rules.codes.requestTypes.cts.value:

        let result = (parts[0] === "DepCTS" &&  vRuc && this.validDate(parts[2]) &&  this.validCurrency(parts[3]));
        console.log('validName DepCTS');
        console.log('result: '+ result);


        return result;

      case constants.rules.codes.requestTypes.paymentOfSalaries.value:
        return (
          parts[0] === "DepCS" &&
          vRuc &&
          this.validDate(parts[2]) &&
          this.validCurrency(parts[3])
        );

      default:
        return false;
    }
  }

  /**
   * Valida el formato del RUC de la solicitud a cargar y que el mismo coincida con el RUC de la empresa actual (userLogged)
   */
  private validRuc(ruc: string): boolean {
    let rucLogged = this.userLogged.getUserData().idNumber.toString();

    return ruc.length === 11 &&
      ruc.match(this.digitsRegex) &&
      ruc === rucLogged
      ? true
      : false;
  }

  /**
   * Valida que la fecha del nombre del archivo tenga el formato correcto y no sea menor a la actual
   */
  private validDate(date: string): boolean {

    var f = new Date();

    var d:string = f.getDate()+"";
    var m:string = f.getMonth()+1+"";
    var y:string = f.getFullYear()+"";

    if(d.length === 1){
       d = "0"+d; 
    }

    if(m.length === 1){
      m = "0"+m; 
    }

    d = y+m+d;

    return date.length === 8 && date === d && date.match(this.digitsRegex) ? true : false;
  }

  /**
   * Valida que la moneda del nombre del archivo tenga el formato correcto
   */
  private validCurrency(currency: string): boolean {
    return currency.length === 2 && (currency === "MN" || currency === "ME")
      ? true
      : false;
  }

  // =================================================================
  //                          Helpers
  // =================================================================
  /**
   * Obtiene un array de strings (líneas del .txt) a partir del File recibido por parámetro
   */
  async getFileContent(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onloadend = (e) => {
        try {
          const lines = (<string>e.target.result).split(/\r\n/);
          lines.splice(-1, 1);
          resolve(lines);
        } catch (error) {
          reject(error);
        }
      };
    });
  }

  /**
   * Obtiene las partes del nombre del archivo separadas por _ en un arreglo de strings
   */
  private getFileNameParts(): string[] {
    return this.file.name.split(".")[0].split("_");
  }

  /**
   * Mapea una solicitud recibida de DirectorServices a un RequestModel manejado por la app
   */
  private mapRequest(request: any): RequestModel {
    
    try {

      return new RequestModel({
        id: request.id,
        number: request.numero,
        type: request.tipo_solicitud,
        ruc: request.datos_identificacion?.numero,
        currency: request.moneda_name,
        responsible: request.responsable,
        amount: parseFloat(request.monto_solicitado),
        date: new Date(request.fecha.split(".")[0]),
        stateCode: request.estado,
        name: request.nombre_plantilla,
        url: request.url_plantilla,
        totalRecords: request.cantidad_registros,
        paymentMethod: request.desembolsos[0]?.tipo_medio_de_pago,
        receipts: request.documentos?.map(
          (rec: any) =>
            new Receipt({
              id: rec.id,
              number: rec.num_comprobante,
              name: rec.url_pdf,
              operationNumber: rec.num_operacion,
              currency: rec.tipo_moneda_name,
              amount: parseFloat(rec.monto),
              receivingBank: rec.banco_receptor,
              issuingBank: rec.banco_emisor,
              issuingBankNumber: rec.cuenta_banco,
              observation: rec.descr_observaciones,
              transferDate: helpers.dateFromReceipt(rec.fecha_pago),
            })
        ),
      });
      
    } catch (error) {
      
    }
    
    
  }

  /**
   * Helper para obtener el body necesario para la creación de una nueva solicitud
   */
  private getCreateRequestBody(user: UserLogged): FormData {
   
    const data: FormData = new FormData();
    data.append("tipo_solicitud", this.type.toString());
    data.append("moneda", this.request.currency === "Soles" ? "1" : "2");
    data.append("monto", this.request.amount.toString());
    data.append("nombre_archivo", Date.now()+"_"+this.request.name);
    data.append("cantidad_registros", this.request.totalRecords.toString());
    data.append("fecha_carga", this.request.date.toString());
    data.append("ruc_empresa", this.request.ruc);
    data.append("tipo_identificacion", "6");
    data.append("razon_social", this.request.companyName);
    data.append("num_serie_empresa", null);
    data.append("email_solicitante", user.getUserData().email);
    data.append("tipo_tel_solicitante", null);
    data.append("cod_area_tel_solicitante", null);
    data.append("num_tel_solicitante", null);
    data.append(
      "usuario_creacion",
      user.isExecutive()
        ? user.getUserData().firstName
        : `${user.getUserData().firstName} ${user.getUserData().lastName}`
    ); //No contamos con el apellido de los usuarios ejecutivos
 
    data.append("file",new File([this.file],Date.now()+"_"+this.file.name));
    return data;
  }

  /**
   * Helper para obtener el body necesario para la creación de un nuevo comprobante
   */
  private getCreateReceiptBody(
    request: RequestModel,
    receipt: Receipt,
    user: UserLogged
  ): FormData {
    const data: FormData = new FormData();
    data.append("file", receipt.file);
    data.append("nombre_archivo", receipt.name);
    data.append("banco_emisor", receipt.issuingBank);
    data.append("nro_cuenta", receipt.issuingBankNumber.toString());
    data.append("banco_receptor", receipt.receivingBank);
    data.append(
      "fecha_transferencia",
      helpers.dateToReceipt(receipt.transferDate)
    );
    data.append("monto", receipt.amount.toString());
    data.append("moneda", receipt.currency === "Soles" ? "1" : "2");
    data.append("nro_operacion", receipt.operationNumber.toString());
    data.append("obervaciones", receipt.observation);
    data.append("id_solicitud", request.id);
    data.append("tipo_solicitud", request.type.value.toString());
    data.append("name_tipo_solicitud", "RECLAMO");
    data.append("tipo_identificacion", "6");
    data.append("nro_identificacion", user.getUserData().idNumber.toString());
    return data;
  }

  /**
   * Helper para obtener el body necesario para la modificación de un comprobante
   */
  private getUpdateReceiptBody(
    request: RequestModel,
    receipt: Receipt
  ): FormData {
    const data: FormData = new FormData();
    data.append("id_solicitud", request.id);
    data.append("id_comprabante", receipt.id);
    data.append("banco_emisor", receipt.issuingBank);
    data.append("nro_cuenta", receipt.issuingBankNumber.toString());
    data.append("banco_receptor", receipt.receivingBank);
    data.append(
      "fecha_transferencia",
      helpers.dateToReceipt(receipt.transferDate)
    );
    data.append("monto", receipt.amount.toString());
    data.append(
      "moneda",
      receipt.currency.toUpperCase() === "SOLES" ? "1" : "2"
    );
    data.append("nro_operacion", receipt.operationNumber.toString());
    data.append("obervaciones", receipt.observation);
    return data;
  }

  /**
   * Helper para obtener en blob el contenido del archivo obtenido del DirectorServices
   */
  private dataURItoBlob(dataURI, type) {
    const byteString = window.atob(dataURI);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const int8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([int8Array], { type });
    return blob;
  }
}
